import { useState } from "react";
import { ArrowDown, ArrowUp, Plus, Trash2, Upload } from "lucide-react";
import { uploadImage } from "./api";

const inputCls =
  "mt-1 block w-full rounded-lg border border-sage/25 bg-white px-3 py-2.5 text-sm text-ink focus:border-sage focus:outline-none focus:ring-2 focus:ring-sage-light/40";

export const FieldInput = ({ field, value, onChange, idPrefix }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const id = `${idPrefix}-${field.name}`;

  if (field.type === "toggle") {
    return (
      <label
        htmlFor={id}
        className="flex min-h-[52px] cursor-pointer items-center justify-between gap-4 rounded-lg border border-sage/15 bg-paper/60 px-4 py-3"
      >
        <span className="text-sm font-semibold text-ink">
          {field.label}
          {field.hint && <span className="block text-xs font-normal text-muted-foreground">{field.hint}</span>}
        </span>
        <input
          id={id}
          type="checkbox"
          checked={!!value}
          onChange={(e) => onChange(e.target.checked)}
          className="h-5 w-5 accent-[#05294E]"
          data-testid={`${idPrefix}-${field.name}-toggle`}
        />
      </label>
    );
  }

  const handleUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    setUploadError("");
    try {
      const url = await uploadImage(f);
      onChange(url);
    } catch (err) {
      setUploadError(err.message);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div>
      <label htmlFor={id} className="block text-sm font-semibold text-ink">
        {field.label}
      </label>
      {field.hint && <p className="mt-0.5 text-xs text-muted-foreground">{field.hint}</p>}
      {field.type === "textarea" ? (
        <textarea
          id={id}
          rows={field.rows || 3}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          className={inputCls}
          data-testid={`${idPrefix}-${field.name}-input`}
        />
      ) : (
        <input
          id={id}
          type={field.type === "url" ? "url" : "text"}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          className={inputCls}
          data-testid={`${idPrefix}-${field.name}-input`}
        />
      )}
      {field.image && (
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <label
            className="inline-flex min-h-[44px] cursor-pointer items-center gap-2 rounded-full border border-sage/30 px-4 py-2 text-sm font-semibold text-sage transition-colors hover:bg-sage-soft"
            data-testid={`${idPrefix}-${field.name}-upload`}
          >
            <Upload className="h-4 w-4" aria-hidden="true" />
            {uploading ? "Uploading…" : "Upload a picture from this device"}
            <input type="file" accept="image/*" className="sr-only" onChange={handleUpload} aria-label={`Upload image for ${field.label}`} />
          </label>
          {typeof value === "string" && value && (
            <img src={value} alt="Current image preview" className="h-14 w-14 rounded-lg border border-sage/15 object-cover" />
          )}
          {uploadError && (
            <p role="alert" className="text-xs font-medium text-red-700">{uploadError}</p>
          )}
        </div>
      )}
      {(value === null || value === "") && field.placeholder !== false && (
        <p className="mt-1 text-xs font-medium text-plum-dark" data-testid={`${idPrefix}-${field.name}-placeholder-flag`}>
          Placeholder — the public site shows a "coming soon" state here.
        </p>
      )}
    </div>
  );
};

export const FieldsPart = ({ schema, value, onChange, idPrefix }) => (
  <div className="space-y-5">
    {schema.map((f) => (
      <FieldInput
        key={f.name}
        field={f}
        value={value?.[f.name]}
        onChange={(v) => onChange({ ...value, [f.name]: v })}
        idPrefix={idPrefix}
      />
    ))}
  </div>
);

export const ListPart = ({ name, label, itemSchema, items, onChange, addLabel = "Add item", itemTitle, idPrefix }) => {
  const list = Array.isArray(items) ? items : [];
  const setItem = (i, patch) => onChange(list.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    const copy = [...list];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    onChange(copy);
  };
  const remove = (i) => {
    const title = itemTitle ? itemTitle(list[i]) : `item ${i + 1}`;
    if (window.confirm(`Delete "${title}"? This cannot be undone once published.`)) {
      onChange(list.filter((_, idx) => idx !== i));
    }
  };
  const add = () => {
    const blank = {};
    itemSchema.forEach((f) => (blank[f.name] = f.type === "toggle" ? true : ""));
    onChange([...list, blank]);
  };
  return (
    <div data-testid={`${idPrefix}-${name || "items"}-list`}>
      {label && <h3 className="mb-4 font-serif text-xl font-medium text-ink">{label}</h3>}
      <div className="space-y-4">
        {list.map((item, i) => (
          <div key={i} className="rounded-xl border border-sage/15 bg-white p-4 sm:p-5">
            <div className="mb-4 flex items-center justify-between gap-3">
              <p className="text-sm font-semibold text-sage">
                {i + 1}. {itemTitle ? itemTitle(item) : `Item ${i + 1}`}
              </p>
              <div className="flex items-center gap-1">
                <button type="button" onClick={() => move(i, -1)} disabled={i === 0} aria-label={`Move item ${i + 1} up`} className="inline-flex min-h-[40px] min-w-[40px] items-center justify-center rounded-md text-body hover:bg-paper disabled:opacity-30">
                  <ArrowUp className="h-4 w-4" aria-hidden="true" />
                </button>
                <button type="button" onClick={() => move(i, 1)} disabled={i === list.length - 1} aria-label={`Move item ${i + 1} down`} className="inline-flex min-h-[40px] min-w-[40px] items-center justify-center rounded-md text-body hover:bg-paper disabled:opacity-30">
                  <ArrowDown className="h-4 w-4" aria-hidden="true" />
                </button>
                <button type="button" onClick={() => remove(i)} aria-label={`Delete item ${i + 1}`} className="inline-flex min-h-[40px] min-w-[40px] items-center justify-center rounded-md text-red-700 hover:bg-red-50" data-testid={`${idPrefix}-${name || "item"}-${i}-delete`}>
                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {itemSchema.map((f) => (
                <div key={f.name} className={f.type === "textarea" || f.type === "toggle" ? "sm:col-span-2" : ""}>
                  <FieldInput
                    field={f}
                    value={item[f.name]}
                    onChange={(v) => setItem(i, { [f.name]: v })}
                    idPrefix={`${idPrefix}-${name || "item"}-${i}`}
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={add}
        className="mt-4 inline-flex min-h-[44px] items-center gap-2 rounded-full border border-dashed border-sage/40 px-5 py-2.5 text-sm font-semibold text-sage hover:bg-sage-soft"
        data-testid={`${idPrefix}-${name || "item"}-add`}
      >
        <Plus className="h-4 w-4" aria-hidden="true" /> {addLabel}
      </button>
    </div>
  );
};
